/**
 * VerifyEmail route — Phase J landing page for the verification email link.
 *
 * URL: /verify-email?email=<addr>&token=<plaintext>
 *
 * The token in the URL is the plaintext; the DB stores SHA-256(plaintext).
 * The RPC `core.confirm_email_verification(p_email, p_token)` hashes the
 * supplied plaintext and matches against the stored hash + expiry.
 *
 * The Verify movement is two-sided in time:
 *   - The Orchist's Add was a claim (one-sided event at T1).
 *   - The email recipient's click here is the attestation (T2).
 *   - Together they form a binding — the candidate becomes verified.
 *
 * No auth required to land here — the token IS the auth. This lets the
 * email holder confirm without signing in to The Line first. (In fact,
 * the recipient might not be the same human who's signed in — they're
 * just the holder of the email being attested.)
 *
 * After success, the user can choose to enter The Line (if they want
 * to also become an Orchist via invitation) or simply close. The
 * candidate connection now exists as a real two-sided fact in the
 * Orchist's record, regardless of what the email-holder does next.
 */

import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

type State =
  | { kind: 'loading' }
  | { kind: 'success'; email: string }
  | { kind: 'invalid' }
  | { kind: 'error'; message: string };

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const email = params.get('email')?.trim() || null;
  const token = params.get('token')?.trim() || null;
  const [state, setState] = useState<State>({ kind: 'loading' });

  useEffect(() => {
    if (!email || !token) {
      setState({ kind: 'invalid' });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase
          .schema('core')
          .rpc('confirm_email_verification', { p_email: email, p_token: token });
        if (cancelled) return;
        if (error) {
          setState({ kind: 'error', message: error.message });
          return;
        }
        const result = data as { verified?: boolean; email?: string } | null;
        if (result?.verified) {
          setState({ kind: 'success', email: result.email ?? email });
        } else {
          setState({ kind: 'error', message: 'Verification did not complete.' });
        }
      } catch (err) {
        if (cancelled) return;
        setState({
          kind: 'error',
          message: err instanceof Error ? err.message : 'Unexpected error.',
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [email, token]);

  return (
    <Shell>
      {state.kind === 'loading' && (
        <>
          <p className="text-[0.7rem] tracking-[0.3em] uppercase text-ink/50">The Line</p>
          <h1 className="display text-3xl md:text-4xl font-medium italic text-ink/70">
            Attesting the connection...
          </h1>
        </>
      )}

      {state.kind === 'invalid' && (
        <>
          <p className="text-[0.7rem] tracking-[0.3em] uppercase text-ink/50">The Line</p>
          <h1 className="display text-4xl md:text-5xl font-medium leading-[1.05]">
            <span className="italic text-accent">A link</span>
            <br />
            without a key.
          </h1>
          <p className="text-lg text-ink/70 leading-relaxed max-w-md mx-auto font-display italic">
            The verification URL is incomplete. Open the link from your
            email exactly as it was sent.
          </p>
        </>
      )}

      {state.kind === 'success' && (
        <>
          <p className="text-[0.7rem] tracking-[0.3em] uppercase text-ink/50">
            The connection holds
          </p>
          <h1 className="display text-4xl md:text-5xl font-medium leading-[1.05]">
            <span className="italic text-accent">Attested.</span>
          </h1>
          <p className="text-lg text-ink/70 leading-relaxed max-w-md mx-auto font-display italic">
            <span className="font-mono not-italic text-base text-ink">{state.email}</span>
            <br />
            now stands as a verified connection in an Orchist's record. The
            two-sided fact exists in time. The body of memory holds it.
          </p>
          <div className="pt-4 flex items-center justify-center gap-6 text-sm text-ink/60">
            <Link
              to="/"
              className="underline underline-offset-4 decoration-ink/20 hover:decoration-ink/60"
            >
              Visit The Line
            </Link>
            <span className="text-ink/30">&middot;</span>
            <a
              href="https://orchist.org"
              className="underline underline-offset-4 decoration-ink/20 hover:decoration-ink/60"
            >
              Read the Manifesto
            </a>
          </div>
        </>
      )}

      {state.kind === 'error' && (
        <>
          <p className="text-[0.7rem] tracking-[0.3em] uppercase text-ink/50">The Line</p>
          <h1 className="display text-3xl md:text-4xl font-medium leading-tight">
            <span className="italic text-accent">The attestation faltered.</span>
          </h1>
          <p className="text-sm text-ink/55 italic font-display max-w-md mx-auto leading-relaxed">
            {state.message}
          </p>
          <p className="text-sm text-ink/55 italic font-display max-w-md mx-auto leading-relaxed">
            The link may have expired or already been used. Ask the Orchist
            who added you to send a fresh verification.
          </p>
          <div className="pt-4">
            <Link
              to="/"
              className="inline-block px-6 py-3 border border-ink/30 text-ink/80 font-display italic tracking-wide hover:border-ink/60 hover:text-ink transition-colors"
            >
              Return to The Line
            </Link>
          </div>
        </>
      )}
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <main className="min-h-screen flex flex-col justify-center items-center px-6 py-24">
      <div
        className="pointer-events-none fixed inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            'radial-gradient(circle at 30% 20%, #8b7ab8 0%, transparent 55%), radial-gradient(circle at 70% 80%, #c9a961 0%, transparent 55%)',
        }}
      />
      <div className="container max-w-2xl text-center space-y-8 relative">{children}</div>
    </main>
  );
}
